import {
   createAsyncThunk,
   createSelector,
   createSlice,
   PayloadAction,
} from "@reduxjs/toolkit"
import {
   addFacilityToCustomer as addCustomerFacility,
   removeFacilityFromCustomer as removeCustomerFacility,
   updateFacility as updateCustomerFacility,
   updateFacilityByCustomer as updateCustomerFacilityByCustomer,
} from "./customersSlice"
import { resetLevelsConfigurations } from "./levelConfigurationsSlice"
import {
   addFacilityToCustomer as addUserFacility,
   updateFacility as updateUserFacility,
   removeFacilityFromCustomer as removeUserFacility,
} from "./userSlice"
import { RootState } from "."
import { ApiError, ApiResponse } from "@/api"
import { CustomerResponse, requestUpdateCustomer } from "@/api/CustomersService"
import {
   requestCreateFacility,
   FacilityResponse,
   requestDeleteFacility,
   requestUpdateFacility,
   requestFacilities,
   requestFacilityDetails,
   requestEntraConsentUrl,
   EntraConsentResponse,
} from "@/api/FacilitiesService"
import { LevelSettingType } from "@/api/FacilityLevelsService"
import { SensorResponse } from "@/api/SensorsService"
import { HttpCodes } from "@/constants/globals"
import { CustomerForm } from "@/types/customer"
import {
   Facility,
   FacilityDetails,
   FacilityForm,
   FacilityLevelDropdown,
   MeasurementSystem,
} from "@/types/facility"
import { Token } from "@/types/index"
import {
   buildLevelOptions,
   buildLevelSelectors,
   getPersistedLevelSelectors,
   persistLevelSelectors,
} from "@/utils/facilities"
import { getBrowserTimezone, isValidFacilityTimezone } from "@/utils/index"
import { isArray, isEmpty } from "@/utils/lodash"

// Read once: Intl is costly and the selector below runs on every store update
const browserTimezone = getBrowserTimezone()

interface FacilitiesState {
   loading: boolean
   error: number | null
   facilities: Facility[]
   loadingFacility: boolean
   errorFacility: number | null
   facilityDetails: FacilityDetails
   facilityLevelSelectors: FacilityLevelDropdown[]
}

export const initialState: FacilitiesState = {
   loading: false,
   error: null,
   facilities: [],
   loadingFacility: false,
   errorFacility: null,
   facilityDetails: {} as FacilityDetails,
   facilityLevelSelectors: [] as FacilityLevelDropdown[],
}

export const deleteFacility = createAsyncThunk(
   "facilities/deleteFacility",
   async (facilityId: string, thunkAPI) => {
      try {
         const state = thunkAPI.getState() as RootState
         const customers = state.customersReducer.customers
         const customer = customers.find((customer) =>
            customer.facilities.some((f) => f.id === facilityId)
         )
         if (!customer) {
            return thunkAPI.rejectWithValue(HttpCodes.BAD_REQUEST)
         }
         const response: ApiResponse<void> = await requestDeleteFacility(
            facilityId,
            customer.id
         )
         if (response.status === HttpCodes.NO_CONTENT) {
            thunkAPI.dispatch(
               removeCustomerFacility({
                  customerId: customer.id,
                  facilityId,
               })
            )
            thunkAPI.dispatch(
               removeUserFacility({ customerId: customer.id, facilityId })
            )
            const activeFacilityId = sessionStorage.getItem(Token.FACILITY_ID)
            if (activeFacilityId === facilityId) {
               sessionStorage.removeItem(Token.FACILITY_ID)
               thunkAPI.dispatch(resetFacilityDetails())
               thunkAPI.dispatch(resetFacilityLevelSelectors())
               thunkAPI.dispatch(resetLevelsConfigurations())
            }
         }
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const createFacility = createAsyncThunk(
   "facilities/createFacility",
   async (
      {
         customerId,
         facilityData,
      }: { customerId: string; facilityData: FacilityForm },
      thunkAPI
   ) => {
      try {
         const response: ApiResponse<FacilityResponse> =
            await requestCreateFacility(customerId, facilityData)

         if (response.status === HttpCodes.CREATED) {
            thunkAPI.dispatch(
               addCustomerFacility({
                  customerId,
                  facilityData: response.data,
               })
            )

            thunkAPI.dispatch(
               addUserFacility({
                  customerId,
                  facilityData: response.data,
               })
            )
         }
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const updateFacility = createAsyncThunk(
   "facilities/updateFacility",
   async (
      {
         facilityId,
         facilityData,
      }: { facilityId: string; facilityData: FacilityForm },
      thunkAPI
   ) => {
      try {
         const state = thunkAPI.getState() as RootState
         const customers = state.customersReducer.customers
         const customer = customers.find((customer) =>
            customer.facilities.some((f) => f.id === facilityId)
         )
         if (!customer) {
            return thunkAPI.rejectWithValue(HttpCodes.BAD_REQUEST)
         }

         const response: ApiResponse<FacilityResponse> =
            await requestUpdateFacility(customer.id, facilityId, facilityData)

         if (response.status === HttpCodes.OK) {
            thunkAPI.dispatch(
               updateCustomerFacility({
                  facilityId,
                  facilityData: response.data,
               })
            )

            thunkAPI.dispatch(
               updateUserFacility({
                  facilityId,
                  facilityData: response.data,
               })
            )
         }
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const updateFacilityByCustomer = createAsyncThunk(
   "facilities/updateFacilityByCustomer",
   async (
      {
         customerId,
         facilityId,
         customerData,
      }: { customerId: string; facilityId: string; customerData: CustomerForm },
      thunkAPI
   ) => {
      try {
         const response: ApiResponse<CustomerResponse> =
            await requestUpdateCustomer(customerId, customerData)
         if (response.status === HttpCodes.OK) {
            thunkAPI.dispatch(
               updateCustomerFacilityByCustomer({
                  facilityId,
                  facilityData: response.data,
               })
            )
         }
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const fetchEntraConsentUrl = createAsyncThunk(
   "facilities/fetchEntraConsentUrl",
   async (facilityId: string, thunkAPI) => {
      try {
         const state = thunkAPI.getState() as RootState
         const customers = state.customersReducer.customers
         const customer = customers.find((customer) =>
            customer.facilities.some((f) => f.id === facilityId)
         )
         if (!customer) {
            return thunkAPI.rejectWithValue(HttpCodes.BAD_REQUEST)
         }

         const response: ApiResponse<EntraConsentResponse> =
            await requestEntraConsentUrl(customer.id, facilityId)

         if (response.status === HttpCodes.OK) {
            thunkAPI.dispatch(
               updateCustomerFacility({
                  facilityId,
                  facilityData: response.data,
               })
            )
         }
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const fetchFacilities = createAsyncThunk(
   "facilities/fetchFacilities",
   async (_, thunkAPI) => {
      try {
         const response: ApiResponse<Facility[]> = await requestFacilities()
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const fetchFacility = createAsyncThunk<
   ApiResponse<FacilityDetails>,
   void,
   { state: RootState }
>(
   "facilities/fetchFacility",
   async (_, thunkAPI) => {
      try {
         const response: ApiResponse<FacilityDetails> =
            await requestFacilityDetails()
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   },
   {
      condition: (_, { getState }) => {
         const { loadingFacility, facilityDetails } =
            getState().facilitiesReducer
         return !loadingFacility && isEmpty(facilityDetails)
      },
   }
)

const facilitiesSlice = createSlice({
   name: "facilities",
   initialState,
   reducers: {
      resetFacilityDetails: (state) => {
         state.facilityDetails = {} as FacilityDetails
      },
      createLevel: (state, action) => {
         if (!isArray(action.payload)) return

         action.payload.forEach((incomingLevel) => {
            const existingIndex = state.facilityDetails.levels.findIndex(
               (level) => level.id === incomingLevel.id
            )

            if (existingIndex === -1) {
               state.facilityDetails.levels.push(incomingLevel)
            } else {
               state.facilityDetails.levels[existingIndex] = incomingLevel
            }
         })

         if (state.facilityDetails.levels.length === 1) {
            state.facilityDetails.levelStructureLocked = true
         }
      },
      updateLevel: (state, action) => {
         const facilityLevelId = state.facilityDetails.levels.findIndex(
            (level) => level.id === action.payload.id
         )
         if (facilityLevelId !== -1) {
            state.facilityDetails.levels[facilityLevelId] = action.payload
         }
      },
      updateLevelsSettings: (state, action) => {
         Object.assign(state.facilityDetails.settings, action.payload.settings)
      },
      deleteLevel: (
         state,
         action: PayloadAction<{ levelId: string; parentId: string | null }>
      ) => {
         const facilityLevelId = state.facilityDetails.levels.findIndex(
            (level) => level.id === action.payload.levelId
         )
         if (facilityLevelId !== -1) {
            const isLevelWithMultiParents =
               state.facilityDetails.levels[facilityLevelId]?.parents.length > 1

            if (isLevelWithMultiParents) {
               state.facilityDetails.levels[facilityLevelId].parents =
                  state.facilityDetails.levels[facilityLevelId].parents.filter(
                     (parentId) => parentId !== action.payload.parentId
                  )

               return
            }

            state.facilityDetails.levels.splice(facilityLevelId, 1)
         }
      },
      updateFacilityLevelSelectorsByIndex: (
         state,
         action: PayloadAction<{ index: number; value: string }>
      ) => {
         const { index, value } = action.payload
         const levels = state.facilityDetails.levels
         const previousValues = state.facilityLevelSelectors

         const updatedValues = previousValues.reduce((acc, current, i) => {
            if (i < index) {
               acc.push(current)
            } else if (i === index) {
               acc.push({
                  ...current,
                  activeValue: value,
               })
            } else {
               const parentId = acc[i - 1].activeValue
               const options = buildLevelOptions(levels, parentId)

               acc.push({
                  typeId: current.typeId,
                  label: current.label,
                  activeValue: options[0]?.value,
                  options,
                  icon: options[0]?.icon,
               })
            }

            return acc
         }, [] as FacilityLevelDropdown[])
         state.facilityLevelSelectors = updatedValues
         persistLevelSelectors(updatedValues)
      },
      resetFacilityLevelSelectors: (state) => {
         state.facilityLevelSelectors = []
         sessionStorage.removeItem(Token.FACILITY_LEVEL_SELECTORS)
      },
      updateFacilityLevelSelectors: (state) => {
         const { levelTypes, levels } = state.facilityDetails
         if (!levels) return

         const levelSelectors = buildLevelSelectors(
            levelTypes,
            levels,
            getPersistedLevelSelectors()
         )
         state.facilityLevelSelectors = levelSelectors
         persistLevelSelectors(levelSelectors)
      },
      createLevelType: (state, action) => {
         state.facilityDetails.levelTypes.push(action.payload)
      },
      updateLevelType: (state, action) => {
         const { id, name, icon } = action.payload
         const prevLevelTypes = state.facilityDetails.levelTypes
         state.facilityDetails.levelTypes = prevLevelTypes.map((levelType) =>
            levelType.id === id
               ? {
                    ...levelType,
                    name: name,
                    icon: icon,
                 }
               : levelType
         )
      },
      updateLevelTypes: (state, action) => {
         state.facilityDetails.levelTypes = [...action.payload].sort(
            (a, b) => a.depth - b.depth
         )
      },
      upsertFacilityLevelSensor: (
         state,
         action: PayloadAction<Partial<SensorResponse>>
      ) => {
         const levelIndex = state.facilityDetails.levels.findIndex(
            (level) => level.id === action.payload.level
         )

         if (levelIndex !== -1) {
            const sensorIndex = state.facilityDetails.levels[
               levelIndex
            ].sensors.findIndex((sensor) => sensor.id === action.payload.id)
            if (sensorIndex !== -1) {
               state.facilityDetails.levels[levelIndex].sensors[sensorIndex] = {
                  id: action.payload.id!,
                  serialNumber: action.payload.serialNumber!,
                  macAddress: action.payload.macAddress!,
                  type: action.payload.type!,
               }
               return
            }

            state.facilityDetails.levels[levelIndex].sensors.push({
               id: action.payload.id!,
               serialNumber: action.payload.serialNumber!,
               macAddress: action.payload.macAddress!,
               type: action.payload.type!,
            })
         }
      },
      deleteFacilityLevelSensor: (
         state,
         action: PayloadAction<{ id: string }>
      ) => {
         const { id } = action.payload

         const levelIndex = state.facilityDetails.levels.findIndex((level) =>
            level.sensors.some((sensor) => sensor.id === id)
         )

         if (levelIndex !== -1) {
            state.facilityDetails.levels[levelIndex].sensors =
               state.facilityDetails.levels[levelIndex].sensors.filter(
                  (sensor) => sensor.id !== id
               )
         }
      },
      setLevelCustomConfigurations: (
         state,
         action: PayloadAction<{
            levelId: string
            hasCustomConfigurations: boolean
         }>
      ) => {
         if (!state.facilityDetails?.levels) return
         const level = state.facilityDetails.levels.find(
            (l) => l.id === action.payload.levelId
         )
         if (level)
            level.hasCustomConfigurations =
               action.payload.hasCustomConfigurations
      },
   },
   extraReducers: (builder) => {
      builder
         .addCase(deleteFacility.pending, (state) => {
            state.loadingFacility = true
            state.errorFacility = null
         })
         .addCase(deleteFacility.fulfilled, (state) => {
            state.loadingFacility = false
            state.errorFacility = null
         })
         .addCase(deleteFacility.rejected, (state, action) => {
            state.loadingFacility = false
            state.errorFacility = action.payload as number
         })
         .addCase(createFacility.pending, (state) => {
            state.loadingFacility = true
            state.errorFacility = null
         })
         .addCase(createFacility.fulfilled, (state) => {
            state.loadingFacility = false
            state.errorFacility = null
         })
         .addCase(createFacility.rejected, (state, action) => {
            state.loadingFacility = false
            state.errorFacility = action.payload as number
         })
         .addCase(updateFacility.pending, (state) => {
            state.loadingFacility = true
            state.errorFacility = null
         })
         .addCase(updateFacility.fulfilled, (state, action) => {
            state.loadingFacility = false
            state.errorFacility = null
            if (
               state.facilityDetails?.id === action.payload.data?.id &&
               action.payload.data?.name
            ) {
               state.facilityDetails.name = action.payload.data.name
            }
         })
         .addCase(updateFacility.rejected, (state, action) => {
            state.loadingFacility = false
            state.errorFacility = action.payload as number
         })
         .addCase(updateFacilityByCustomer.pending, (state) => {
            state.loadingFacility = true
            state.errorFacility = null
         })
         .addCase(updateFacilityByCustomer.fulfilled, (state, action) => {
            state.loadingFacility = false
            state.errorFacility = null
            const updatedFacility = action.payload.data?.facilities?.find(
               (f) => f.id === state.facilityDetails?.id
            )
            if (updatedFacility?.name) {
               state.facilityDetails.name = updatedFacility.name
            }
         })
         .addCase(updateFacilityByCustomer.rejected, (state, action) => {
            state.loadingFacility = false
            state.errorFacility = action.payload as number
         })
         .addCase(fetchFacility.pending, (state) => {
            state.loadingFacility = true
            state.errorFacility = null
         })
         .addCase(
            fetchFacility.fulfilled,
            (state, action: PayloadAction<ApiResponse<FacilityDetails>>) => {
               state.loadingFacility = false
               const { levelTypes, levels } = action.payload.data
               const levelSelectors = buildLevelSelectors(
                  levelTypes,
                  levels,
                  getPersistedLevelSelectors()
               )

               state.facilityLevelSelectors = levelSelectors
               persistLevelSelectors(levelSelectors)
               Object.assign(state.facilityDetails, {
                  ...action.payload.data,
                  levels: levels.map((level) => ({
                     ...level,
                     parents: level.parents ?? [],
                  })),
                  levelTypes: [...levelTypes].sort((a, b) => a.depth - b.depth),
               })
            }
         )
         .addCase(fetchFacility.rejected, (state, action) => {
            state.loadingFacility = false
            state.errorFacility = action.payload as number
         })
         .addCase(fetchEntraConsentUrl.pending, (state) => {
            state.loadingFacility = true
            state.errorFacility = null
         })
         .addCase(fetchEntraConsentUrl.fulfilled, (state) => {
            state.loadingFacility = false
            state.errorFacility = null
         })
         .addCase(fetchEntraConsentUrl.rejected, (state, action) => {
            state.loadingFacility = false
            state.errorFacility = action.payload as number
         })
         .addCase(fetchFacilities.pending, (state) => {
            state.loading = true
            state.error = null
         })
         .addCase(
            fetchFacilities.fulfilled,
            (state, action: PayloadAction<ApiResponse<Facility[]>>) => {
               state.loading = false
               Object.assign(state.facilities, action.payload.data)
            }
         )
         .addCase(fetchFacilities.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as number
         })
   },
})

export const selectDeepestLevelId = (state: RootState) =>
   state.facilitiesReducer.facilityLevelSelectors.at(-1)?.activeValue

// Memoized on facilityDetails, so the IANA check runs once per facility load
export const selectTimezone = createSelector(
   [(state: RootState) => state.facilitiesReducer.facilityDetails],
   (facilityDetails): string | null => {
      if (isEmpty(facilityDetails.address)) return null

      const { timezone } = facilityDetails.address
      if (isValidFacilityTimezone(timezone)) return timezone

      if (timezone) {
         console.warn(
            "Invalid facility timezone, falling back to the browser timezone:",
            timezone
         )
      }
      return browserTimezone
   }
)

export const selectIsDeviceDefaultTimezone = createSelector(
   [(state: RootState) => state.facilitiesReducer.facilityDetails],
   (facilityDetails): boolean => {
      if (isEmpty(facilityDetails.address)) return false

      return !isValidFacilityTimezone(facilityDetails.address.timezone)
   }
)

export const selectMeasurementSystem = createSelector(
   [(state: RootState) => state.facilitiesReducer.facilityDetails.settings],
   (settings): MeasurementSystem => {
      const setting = settings?.find(
         ({ key }) => key === LevelSettingType.MEASUREMENT_SYSTEM
      )
      return setting?.value === MeasurementSystem.METRIC
         ? MeasurementSystem.METRIC
         : MeasurementSystem.IMPERIAL
   }
)

export const {
   resetFacilityDetails,
   resetFacilityLevelSelectors,
   createLevelType,
   updateLevelType,
   updateLevelTypes,
   createLevel,
   updateLevel,
   deleteLevel,
   updateLevelsSettings,
   updateFacilityLevelSelectors,
   updateFacilityLevelSelectorsByIndex,
   upsertFacilityLevelSensor,
   deleteFacilityLevelSensor,
   setLevelCustomConfigurations,
} = facilitiesSlice.actions

export default facilitiesSlice.reducer
